import { Paper, Text, Group, Stack, Box, Flex, Skeleton, LoadingOverlay, Popover, UnstyledButton } from '@mantine/core';
import { IconArrowUpRight, IconArrowDownRight, IconInfoCircle } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';

interface MetricsCardProps {
  label: string;
  labelDescriptor?: string;
  value: string | number | null;
  previousValue?: string | number | null;
  change?: {
    value: number;
    type: 'increase' | 'decrease';
  };
  suffix?: string;
  comparisonLabel?: string;
  loading?: boolean;
  tooltip?: string;
}

// Use global color variables
const getColor = (type: 'increase' | 'decrease' | undefined) => {
  if (!type) return undefined;
  return type === 'increase' ? 'var(--green-color)' : 'var(--red-color)';
};

export function MetricsCard({ 
  label, 
  labelDescriptor,
  value, 
  previousValue, 
  change, 
  suffix, 
  comparisonLabel = 'prev. 7 days', 
  loading = false,
  tooltip = 'No additional information available'
}: MetricsCardProps) {
  const Icon = change?.type === 'increase' ? IconArrowUpRight : IconArrowDownRight;
  const [popoverOpened, { open: openPopover, close: closePopover }] = useDisclosure(false);
  
  // Handle null/NaN values
  const displayValue = (value === null || isNaN(Number(value))) ? 0 : value;
  const displayPreviousValue = (previousValue === null || isNaN(Number(previousValue))) ? 0 : previousValue;

  return (
    <Paper
      withBorder
      py="xl"
      px="md"
      h="100%"
      radius="md"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        minWidth: '180px', // Ensure minimum width to fit content
      }}
    >
      {/* Loading overlay with spinner */}
      <LoadingOverlay 
        visible={loading} 
        overlayProps={{ blur: 0.5, opacity: 0.4 }}
        loaderProps={{ size: 'sm', color: 'var(--primary-color, blue)' }}
      />
      
      <Flex w="100%" mb="md" h={24}>
        <Box style={{ flex: 1 }} />
        <Box miw="fit-content" px="xs">
          <Text size="sm" c="dimmed" style={{ fontSize: 'var(--label-size, 0.875rem)' }}>
          {label}
          {labelDescriptor && (
            <Text component="span" ml={4} inherit style={{ fontSize: '0.75rem' }}>
              {labelDescriptor}
            </Text>
          )}
        </Text>
        </Box>
        <Flex style={{ flex: 1 }} align="center" pl="xs">
          <Popover
            width={250}
            opened={popoverOpened}
          >
            <Popover.Target>
              <UnstyledButton
                size={22}
                onMouseEnter={openPopover}
                onMouseLeave={closePopover}
                variant="transparent"
                color="var(--text-color)"
              >
                <IconInfoCircle size={18} stroke={1.75} />
              </UnstyledButton>
            </Popover.Target>
            <Popover.Dropdown>
              <Text size="sm">
                {tooltip}
              </Text>
            </Popover.Dropdown>
          </Popover>
        </Flex>
      </Flex>

      <Stack gap="xs" align="center" justify="space-between" h={80}>
        <Box h={42} style={{ display: 'flex', alignItems: 'center' }}>
          {loading ? (
            <Skeleton height={30} width={80} radius="sm" />
          ) : (
          <Text fw={600} style={{ fontSize: 'var(--value-size, 1.5rem)' }}>
            {displayValue}
            {suffix && (
              <Text component="span" size="xs" ml={4} style={{ fontSize: 'var(--descriptor-size, 0.6rem)' }}>
                {suffix}
              </Text>
            )}
          </Text>
          )}
        </Box>
        
        <Box h={20} style={{ display: 'flex', alignItems: 'center' }}>
        {loading ? (
          <Skeleton height={16} width={40} radius="sm" />
        ) : change && (
            <Group gap={4} align="center">
              <Icon size={20} style={{ color: getColor(change.type) }} stroke={2.5} />
              <Text size="sm" style={{ color: getColor(change.type) }}>
                {change.value === 999 ? 'New!' : `${change.value}%`}
            </Text>
          </Group>
        )}
        </Box>

        <Box h={18} style={{ display: 'flex', alignItems: 'center' }}>
          {loading ? (
            <Skeleton height={16} width={120} radius="sm" />
          ) : previousValue !== undefined && (
            <Text size="sm" c="dimmed" ta="center" style={{ fontSize: 'var(--label-size, 0.875rem)' }}>
              vs.{' '}
              <Text component="span" fw={600} inherit>
                {displayPreviousValue}
        </Text>
              {' '}{comparisonLabel}
          </Text>
        )}
        </Box>
      </Stack>

      <style>
        {`
          @media screen and (min-width: 75em) {
            :root {
              --label-size: 0.925rem;
              --value-size: 1.8125rem;
              --descriptor-size: 0.75rem;
            }
          }
          @media screen and (min-width: 88em) {
            :root {
              --label-size: 1rem;
            }
          }
        `}
      </style>
    </Paper>
  );
} 